<template>
  <div class="flex flex-col h-full bg-surface-50">
    <!-- Editing Element -->
    <ConsentFormElementEditor
      v-if="editingElement"
      :element="editingElement"
      @save="handleUpdateElement"
      @cancel="editingElement = null"
    />

    <!-- Builder Main View -->
    <template v-else>
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
        <div class="flex items-center gap-3">
          <input
            v-model="formTitle"
            class="font-bold text-lg text-surface-800 bg-surface-50 border border-surface-200 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 max-w-[200px] md:max-w-[400px] truncate transition-colors"
            placeholder="שם הטופס"
          />
        </div>
        <Button
          label="שמירה"
          icon="pi pi-check"
          size="small"
          @click="handleSave"
          :loading="saving"
        />
      </div>

      <!-- Elements List -->
      <div class="flex-1 overflow-y-auto p-4 pb-24">
        <h2 class="text-xs font-bold text-primary-600 tracking-wider mb-3 uppercase">תצוגה מקדימה</h2>
        <div class="bg-white rounded-2xl shadow-sm border border-surface-100 overflow-hidden">
          <div v-if="elements.length === 0" class="p-8 text-center text-surface-400">
            <i class="pi pi-plus text-4xl mx-auto mb-2 opacity-50 block"></i>
            <p>הטופס ריק. הוסיפו אלמנט ראשון.</p>
          </div>
          <draggable
            v-else
            v-model="elements"
            item-key="id"
            handle=".drag-handle"
            animation="200"
            ghost-class="opacity-30"
            class="divide-y divide-surface-100"
          >
            <template #item="{ element: el }">
              <div
                class="p-4 flex items-start gap-3 hover:bg-surface-50 group transition-colors"
              >
                <!-- Drag Handle -->
                <div
                  class="drag-handle cursor-grab active:cursor-grabbing mt-1 opacity-20 group-hover:opacity-100 touch-none">
                  <i class="pi pi-bars text-surface-400 text-lg"></i>
                </div>

                <!-- Element Info -->
                <div class="flex-1 min-w-0">
                  <h3 class="font-semibold text-surface-800 truncate">{{ el.label }}</h3>
                  <p class="text-xs text-surface-500 flex items-center gap-2 mt-1">
                    <span>{{ elementTypeLabel(el.type) }}</span>
                    <span v-if="el.required" class="text-primary-600">• שדה חובה</span>
                  </p>
                </div>

                <!-- Actions -->
                <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    class="p-2 text-surface-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                    @click="editingElement = { ...el, options: el.options ? [...el.options] : undefined }"
                  >
                    <i class="pi pi-pencil text-base"></i>
                  </button>
                  <button
                    class="p-2 text-surface-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                    @click="handleDeleteElement(el.id)"
                  >
                    <i class="pi pi-trash text-base"></i>
                  </button>
                </div>
              </div>
            </template>
          </draggable>

          <!-- Add Element Button -->
          <div class="p-4 bg-surface-50 border-t border-surface-100 flex justify-center">
            <button
              class="flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 py-2 px-4 rounded-full hover:bg-primary-50 transition-colors"
              @click="showAddDrawer()"
            >
              <i class="pi pi-plus text-lg"></i> הוספת אלמנט
            </button>
          </div>
        </div>

        <!-- Automation Settings -->
        <div class="mt-6">
          <button
            class="flex items-center gap-2 text-xs font-bold text-primary-600 tracking-wider mb-3 uppercase w-full"
            @click="showAutomation = !showAutomation"
          >
            <i :class="showAutomation ? 'pi pi-chevron-down' : 'pi pi-chevron-left'" class="text-xs"></i>
            <i class="pi pi-bolt text-sm"></i>
            <span>אוטומציה - שליחה אוטומטית</span>
          </button>
          <div v-if="showAutomation" class="bg-white rounded-2xl shadow-sm border border-surface-100 p-4 space-y-4">
            <!-- Send to all new users -->
            <label class="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <div class="font-medium text-surface-800 text-sm">שליחה ללקוח חדש</div>
                <div class="text-xs text-surface-500 mt-0.5">הטופס יישלח אוטומטית ללקוחות שמזמינים תור לראשונה</div>
              </div>
              <InputSwitch v-model="automationSendToNewUsers" />
            </label>

            <!-- Send to specific service bookings -->
            <div class="border-t border-surface-100 pt-4">
              <div class="font-medium text-surface-800 text-sm mb-1">שליחה לפי שירות</div>
              <div class="text-xs text-surface-500 mb-3">הטופס יישלח ללקוחות שמזמינים לראשונה אחד מהשירותים הבאים</div>
              <SelectService
                :modelValue="selectedAutomationServices"
                @servicesSelected="onAutomationServicesSelected"
              />
              <div v-if="automationServiceIds.length > 0" class="mt-2 flex flex-wrap gap-1.5">
                <span
                  v-for="svc in selectedAutomationServices"
                  :key="svc.id"
                  class="inline-flex items-center gap-1 text-xs bg-primary-50 text-primary-700 px-2.5 py-1 rounded-full"
                >
                  {{ svc.name }}
                  <button
                    class="hover:text-red-500 transition-colors"
                    @click="automationServiceIds = automationServiceIds.filter(id => id !== svc.id)"
                  >
                    <i class="pi pi-times text-[10px]"></i>
                  </button>
                </span>
              </div>
            </div>

            <!-- Channel the form link goes out on -->
            <div class="border-t border-surface-100 pt-3">
              <div class="font-medium text-surface-800 text-sm mb-1">ערוץ שליחה</div>
              <settings-radio-group
                :modelValue="automationChannel"
                last
                :options="[
                  { value: 'sms', label: 'SMS', desc: 'קישור לטופס נשלח בהודעת SMS' },
                  { value: 'whatsapp', label: 'WhatsApp', desc: 'הודעת WhatsApp עם כפתור למילוי הטופס · אם ההודעה לא נמסרת, נשלח SMS במקומה' },
                ]"
                @update:modelValue="(v) => automationChannel = v"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import draggable from 'vuedraggable';
import SettingsRadioGroup from '../configurations/ui/SettingsRadioGroup.vue';
import type { IConsentForm, IConsentFormAutomation, IConsentFormElement, IService } from '@torim-app/types';
import { ConsentFormElementTypeEnum } from '@torim-app/types';
import { useAdminStore } from '@app/stores';
import ConsentFormElementEditor from './ConsentFormElementEditor.vue';
import SelectService from '@app/components/admin/components/SelectService.vue';
import { useDrawer } from '@torim-app/shared-components';
import AddElementDrawer from '@app/components/admin/pages/consent-forms/AddElementDrawer.vue';

const props = defineProps<{
  form: IConsentForm;
  onSave: (form: Pick<IConsentForm, 'title' | 'elements'> & { automation?: IConsentFormAutomation }) => Promise<void>;
  onClose: () => void;
}>();

const { closeDrawer, openDrawer } = useDrawer();
const adminStore = useAdminStore();
const services = computed(() => adminStore.getServices);

const formTitle = ref(props.form.title);
const elements = ref<IConsentFormElement[]>(JSON.parse(JSON.stringify(props.form.elements)));
const editingElement = ref<IConsentFormElement | null>(null);
const showAutomation = ref(!!(props.form.automation?.sendToNewUsers || props.form.automation?.sendToServiceIds?.length));
const saving = ref(false);

const automationSendToNewUsers = ref(props.form.automation?.sendToNewUsers ?? false);
const automationServiceIds = ref<string[]>(props.form.automation?.sendToServiceIds ?? []);
const automationChannel = ref<'sms' | 'whatsapp'>(props.form.automation?.channel ?? 'sms');

const selectedAutomationServices = computed(() =>
  services.value.filter((s) => automationServiceIds.value.includes(s.id))
);

const onAutomationServicesSelected = (selected: IService[]) => {
  automationServiceIds.value = selected.map((s) => s.id);
};

const onSelectedElement = (newEl: IConsentFormElement) => {
  console.log('here');
  elements.value.push(newEl);
  closeDrawer();
  editingElement.value = { ...newEl, options: newEl.options ? [...newEl.options] : undefined };
}

const showAddDrawer = () => {
  openDrawer(AddElementDrawer, {
    onSelectedElement: onSelectedElement,
  }, {
    title: 'הוספת אלמנט',
    noPadding: false
  })
}

const elementTypeLabel = (type: ConsentFormElementTypeEnum) => {
  const map: Record<ConsentFormElementTypeEnum, string> = {
    [ConsentFormElementTypeEnum.ShortText]: 'שדה טקסט',
    [ConsentFormElementTypeEnum.Checkboxes]: 'תיבות סימון',
    [ConsentFormElementTypeEnum.MultipleChoice]: 'בחירה יחידה',
    [ConsentFormElementTypeEnum.Signature]: 'חתימה',
    [ConsentFormElementTypeEnum.ReadOnly]: 'בלוק טקסט',
    [ConsentFormElementTypeEnum.Image]: 'תמונה'
  };
  return map[type] || type;
};

const handleDeleteElement = (id: string) => {
  elements.value = elements.value.filter((el) => el.id !== id);
};

const handleUpdateElement = (updated: IConsentFormElement) => {
  const index = elements.value.findIndex((e) => e.id === updated.id);
  if (index !== -1) elements.value[index] = updated;
  editingElement.value = null;
};

const handleSave = async () => {
  saving.value = true;
  try {
    await props.onSave({
      title: formTitle.value,
      elements: elements.value,
      automation: {
        sendToNewUsers: automationSendToNewUsers.value,
        sendToServiceIds: automationServiceIds.value,
        channel: automationChannel.value
      }
    });
  } finally {
    saving.value = false;
  }
};
</script>
